import { useEffect } from "react";
import { useThemeStore } from "@/store/theme-store";
import { isAppLike, isAndroid, isNative } from "@/lib/platform";

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { theme, setTheme } = useThemeStore();

  useEffect(() => {
    const root = document.documentElement;
    const native = isAppLike();
    // Android's StatusBar.setOverlaysWebView({overlay:false}) already moves
    // the WebView's frame below the status bar at the OS level, so
    // env(safe-area-inset-top) inside the WebView double-counts that space
    // (it still reports the full status-bar height). iOS has no such API —
    // its WebView always extends under the notch, so it still needs the
    // env() inset. This class lets CSS skip the env() addition on Android only.
    root.classList.toggle("native-android", isAndroid());
    // `native-app` means "wear the app's interface" and is also what the
    // ?appview=1 debug override sets. `native-shell` means "we are genuinely
    // inside a device WebView". Only the second one should attract the
    // compensations a device needs — notably the status-bar floor, which in a
    // browser is 28px of navy nobody asked for.
    root.classList.toggle("native-shell", isNative());
    if (native) {
      root.classList.add("native-app");
      root.classList.remove("dark");
      if (theme === "dark") {
        setTheme("light");
      }
      return;
    }
    root.classList.remove("native-app");
    if (theme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
  }, [theme, setTheme]);

  return <>{children}</>;
}
